// client/src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Carousel from "../components/Carousel";
import CalendarSlots from "../components/CalendarSlots";
import { motion } from "framer-motion";

const Home = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [announcements, setAnnouncements] = useState([]);
    useEffect(() => {
        const fetchHomepage = async () => {
            try {
                const response = await axios.get("/api/homepage");
                setData(response.data);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };
        fetchHomepage();
    }, []);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await axios.get("/api/announcements");
                setAnnouncements(response.data);
            } catch (error) {
                console.error("Error fetching announcements:", error);
            }
        };

        fetchAnnouncements();
    }, []);


    if (loading) return <p>Loading...</p>;
    if (!data) return <p>Homepage content not found.</p>;

    // Define transition properties
    const pageTransition = {
        initial: { opacity: 0, y: 0 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 0 },
        transition: { duration: 5.0 }
    };

    return (
        <motion.div {...pageTransition}>
            <div style={{
                backgroundColor: data.colorScheme.primary,
                color: data.colorScheme.text,
                "paddingBottom": "1pt"
            }}>
                {/* Upper Section with Calendar, Carousel, and Copy */}
                <section className="flex flex-col md:flex-row gap-4 bg-gray-50 py-8 px-4">
                    {/* Calendar Slots */}
                    <div className="w-full md:w-1/3 flex flex-col justify-between">
                        <div className="h-full bg-white rounded-lg shadow p-4 flex flex-col">
                            <CalendarSlots calendarId={data.calendar.calendarId}/>
                            {/* Button to navigate to calendar */}
                            <button
                                onClick={() => (window.location.href = "./get-involved/calendar")}
                                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-3xl shadow hover:bg-blue-600 w-full flex-grow"
                            >
                                View Full Calendar
                            </button>
                        </div>
                    </div>
                    {/* Carousel and Copy */}
                    <div className="w-full md:w-2/3 flex flex-col items-center">
                        <Carousel images={data.heroCarousel}/>
                        {/* Copy Section */}
                        <div className="bg-white py-6 px-6 shadow rounded-lg mt-4 text-center w-full">
                            {/* Render bodyText with support for HTML */}
                            <p
                                className="text-lg leading-relaxed"
                                dangerouslySetInnerHTML={{__html: data.intro.bodyText}}
                            ></p>

                            {/* Render signature with support for HTML */}
                            <p
                                className="text-lg leading-relaxed font-bold"
                                dangerouslySetInnerHTML={{__html: data.intro.signature}}
                            ></p>
                        </div>
                    </div>
                </section>


                {/* Sections */}
                {data.sections.map((section, index) => (
                    <section key={index} className="m-8 py-12 px-8 relative bg-gray-100 shadow-lg rounded-lg">
                        <div className="absolute top-0 left-0 w-full h-full -z-10 bg-red-600 rounded-lg"></div>
                        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
                            {/* Render section image if available */}
                            {section.imageUrl && (
                                <div className="w-full md:w-1/3">
                                    <img
                                        src={section.imageUrl}
                                        alt={section.title}
                                        className="rounded-lg"
                                    />
                                </div>
                            )}

                            {/* Main section content */}
                            <div className="w-full md:w-2/3 text-center md:text-left">
                                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                                {section.subtitle && (
                                    <h3 className="text-lg font-semibold mb-4">{section.subtitle}</h3>
                                )}
                                <div
                                    dangerouslySetInnerHTML={{__html: section.content}}
                                    className="text-lg leading-relaxed"
                                ></div>

                                {/* Render sub-sections if they exist */}
                                {section.subSections && section.subSections.length > 0 && (
                                    <div className="mt-6">
                                        {section.subSections.map((subSection, subIndex) => (
                                            <div
                                                key={subIndex}
                                                className="mt-4 bg-white p-6 rounded-lg shadow-lg"
                                            >
                                                <h4 className="text-xl font-bold">{subSection.title}</h4>
                                                <div
                                                    dangerouslySetInnerHTML={{__html: subSection.text}}
                                                    className="text-md leading-relaxed mt-2"
                                                ></div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Render quotes if available */}
                                {section.quotes && (
                                    <div className="mt-4">
                                        {section.quotes.map((quote, qIndex) => (
                                            <blockquote
                                                key={qIndex}
                                                dangerouslySetInnerHTML={{__html: quote.text}}
                                                className="italic mt-2"
                                            ></blockquote>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                ))}


                {/* Announcements */}
                <section className="py-8 px-8 bg-white shadow rounded-lg m-8">
                    <h2 className="text-2xl font-bold text-center mb-6">Announcements</h2>

                        {announcements.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {announcements.map((announcement) => (
                                    <div key={announcement._id} className="p-4 border rounded-lg bg-gray-100 shadow">
                                        <h3 className="font-bold">{announcement.title}</h3>
                                        <p>{announcement.description}</p>
                                        {announcement.imageUrl && (
                                            <img
                                                src={announcement.imageUrl}
                                                alt={announcement.title}
                                                className="mt-2"
                                            />
                                        )}
                                        {announcement.linkUrl && (
                                            <a
                                                href={announcement.linkUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-500 hover:underline mt-2 inline-block"
                                            >
                                                {announcement.linkText}
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p>No announcements available.</p>
                        )}

                </section>


            </div>
        </motion.div>
            );
            };

            export default Home;
